import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Login from "./pages/login";
import Home from "./pages/home";
import Profile from "./pages/Profile";
import FacultyHome from "./pages/faculty/FacultyHome";
import FacultyProfile from "./pages/faculty/FacultyProfile";
import Students from "./pages/Students"; // <-- Add this
import Admin from "./pages/Admin";
import Explore from "./pages/Explore";
// Events placeholder replaced by new EventsHub module
import Formation from "./pages/Formation";
import TeamRoom from "./pages/TeamRoom";
import ReviewDashboard from "./pages/ReviewDashboard";
import AdminTeams from "./pages/AdminTeams";
import FacultyAdmin from "./pages/faculty/FacultyAdmin";
import FacultyAnnouncements from "./pages/faculty/FacultyAnnouncements";
import FacultyTimetable from "./pages/faculty/FacultyTimetable";
import FacultyAttendance from "./pages/faculty/FacultyAttendance";
import AttendanceAdmin from "./pages/faculty/AttendanceAdmin";
import ScannerAttendance from "./pages/faculty/ScannerAttendance";
import Attendance from "./pages/Attendance";
import FeedbackModal from "./components/FeedbackModal";
import InstallPrompt from "./components/InstallPrompt";
import EventsHub from './pages/events/EventsHub';
import ConvergenceLanding from './pages/events/ConvergenceLanding';
import EventNetworkingHome from './pages/events/EventNetworkingHome';
import EventAdminDashboard from './pages/events/EventAdminDashboard';
import API_URL from './config';
import "./pages/Home.css"; // Ensure global theme styles are available

function GlobalSessionTracker() {
  const location = useLocation();

  React.useEffect(() => {
    try {
      // 1. Strict Theme Guard: ALL routes starting with /events must be strictly in LIGHT THEME
      if (location.pathname.startsWith("/events")) {
        document.documentElement.setAttribute("data-theme", "light");
      } else {
        const savedTheme = localStorage.getItem("vjconnect_theme") || "dark";
        document.documentElement.setAttribute("data-theme", savedTheme);
      }

      // Event-Scoped Security for External Participants:
      // Restrict external participants strictly to /events routes
      const externalUserStr = localStorage.getItem("convergence_external_user");
      const internalUserStr = localStorage.getItem("vjconnect_user");
      if (externalUserStr && !internalUserStr) {
        const pathname = location.pathname;
        const isAllowedEventRoute = pathname.startsWith("/events");
        if (!isAllowedEventRoute) {
          console.warn("[Security Guard] External participant blocked from internal route:", pathname);
          let targetSlug = 'events';
          try {
            const extUser = JSON.parse(externalUserStr);
            if (extUser && extUser.event_slug) targetSlug = `events/${extUser.event_slug}/networking`;
          } catch (e) {}
          window.location.replace(`/${targetSlug}`);
          return;
        }
      }

      const stored = localStorage.getItem("vjconnect_user");
      if (!stored) return;
      const user = JSON.parse(stored);
      if (!user || !user.email) return;

      // 1. Session ping to record active user telemetry in Admin dashboard
      fetch(`${API_URL}/auth/session-ping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, name: user.name, role: user.role })
      }).catch(() => {});

      // 2. Global FCM token registration if push notification permission was granted
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        import("./firebase")
          .then(({ requestForToken }) => requestForToken())
          .then((token) => {
            if (token && user.email) {
              fetch(`${API_URL}/notifications/register-token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: user.email, fcm_token: token, device_info: navigator.userAgent })
              }).catch(() => {});
            }
          })
          .catch(() => {});
      }
    } catch (e) {}
  }, [location.pathname]);

  // 3. Foreground push notification listener
  React.useEffect(() => {
    let unsubscribe = null;
    import("./firebase")
      .then(({ setupForegroundListener }) => {
        unsubscribe = setupForegroundListener(({ title, body, clickAction }) => {
          console.log(`[Foreground Push Notification] ${title}: ${body} -> ${clickAction}`);
        });
      })
      .catch(() => {});

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  return null;
}

function App() {
  // Global Theme Sync
  React.useEffect(() => {
    if (window.location.pathname.startsWith("/events")) {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      const savedTheme = localStorage.getItem("vjconnect_theme") || "dark";
      document.documentElement.setAttribute("data-theme", savedTheme);
    }
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GlobalSessionTracker />
      <InstallPrompt />
      <FeedbackModal />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/home" element={<Home />} />
        <Route path="/faculty/home" element={<FacultyHome />} />
        <Route path="/faculty/admin" element={<FacultyAdmin />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/:rollNo" element={<Profile />} />
        <Route path="/students" element={<Students />} /> {/* <-- Add this */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/teams/:taskId" element={<AdminTeams />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/events" element={<EventsHub />} />
        <Route path="/formation" element={<Formation />} />
        <Route path="/formation/:taskId" element={<Formation />} />
        <Route path="/formation/team/:teamId" element={<TeamRoom />} />
        <Route path="/formation/:taskId/review" element={<ReviewDashboard />} />
        <Route path="/admin/teams/:taskId/team/:teamId" element={<TeamRoom />} />
        <Route path="/faculty/profile" element={<FacultyProfile />} />
        <Route path="/faculty/announcements" element={<FacultyAnnouncements />} />
        <Route path="/faculty/timetable" element={<FacultyTimetable />} />
        <Route path="/faculty/attendance" element={<FacultyAttendance />} />
        <Route path="/faculty/attendance-admin" element={<AttendanceAdmin />} />
        <Route path="/faculty/scanner-attendance" element={<ScannerAttendance />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/events/:slug/networking" element={<EventNetworkingHome />} />
        <Route path="/events/:slug/admin" element={<EventAdminDashboard />} />
        <Route path="/events/:slug" element={<ConvergenceLanding />} />
      </Routes>
    </Router>
  );
}

export default App;
